import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { toast } from 'react-toastify';

import { DropDownOptions } from 'devextreme-react/autocomplete';
import Form, { Item, RequiredRule } from 'devextreme-react/form';
import ValidationGroup from 'devextreme-react/validation-group';
import Validator from 'devextreme-react/validator';

import { AppCol, AppFieldBox, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { MONEY_FORMAT } from 'constants/formats';
import { Button, DateBox, NumberBox, SelectBox, TextArea } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';

import {
  useChangeCashFlowExpenseAccountMutation,
  useCreateCashFlowExpenseAccountMutation,
  useGetCashFlowAccountsQuery,
  useLazyGetCashFlowExpenseAccountQuery,
} from 'bus/cash';

import { initValue } from './utils';

type Props = {
  id: string;
  cashFlowId: number;
};

const CashFlowExpenseAccount: FC<Props> = ({ id, cashFlowId }) => {
  const history = useHistory();
  const [form, setForm] = useState(initValue);
  const createValidationRef = useRef<ValidationGroup>(null);

  const [changeCashFlowExpenseAccount, { isLoading: isLoadingChange }] = useChangeCashFlowExpenseAccountMutation();
  const [createCashFlowExpenseAccount, { isLoading: isLoadingCreate }] = useCreateCashFlowExpenseAccountMutation();
  const [getCashFlowExpenseAccountQuery] = useLazyGetCashFlowExpenseAccountQuery();
  const { data: cashFlowAccounts } = useGetCashFlowAccountsQuery();

  const disabledSubmit = isLoadingChange || isLoadingCreate;

  const handleSubmit = async (e: any) => {
    e.preventDefault && e.preventDefault();

    if (createValidationRef.current?.instance.validate().isValid) {
      if (id) {
        await changeCashFlowExpenseAccount({ id, ...form });
        toast.success('Изменения успешно сохранены', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        await createCashFlowExpenseAccount({ ...form, cashFlowId });
        history.push('/cash-flow');
      }
    }
  };

  const contractorDataStore = useMemo(() => {
    const defaultFilter = ['isProvider', '=', true];
    return $createDataSource('/v1/contractor', 'id', { defaultFilter });
  }, []);

  const requestDateSource = useMemo(() => {
    const defaultFilter = ['cashFlowType', '=', '1'];
    return $createDataSource('/v1/CashFlowItem', 'id', { defaultFilter });
  }, []);

  const getInit = useCallback(async () => {
    if (id) {
      const result = await getCashFlowExpenseAccountQuery({ id });
      result && setForm(result.data!);
    }
  }, [id, getCashFlowExpenseAccountQuery]);

  useEffect(() => {
    getInit();
  }, [getInit]);

  return (
    <div className="mt-5" style={{ width: 750 }}>
      <form onSubmit={handleSubmit}>
        <ValidationGroup ref={createValidationRef}>
          <AppLayoutTop title="Расход с расчетного счета">
            <ClaimsProvider claimsBlock={['CanEditExpenseAccount']}>
              <Button
                text="Сохранить"
                type="default"
                useSubmitBehavior={false}
                onClick={handleSubmit}
                disabled={disabledSubmit}
              />
            </ClaimsProvider>
          </AppLayoutTop>
          <div className="dx-card">
            <Form formData={form}>
              <Item colSpan={12} colCount={12} itemType="group">
                <AppCol col={4}>
                  <AppFieldBox label="Дата" labelTop>
                    <DateBox
                      stylingMode="underlined"
                      type="date"
                      onValueChange={(e) => setForm((prev) => ({ ...prev, date: e as string }))}
                      useMaskBehavior
                      dateSerializationFormat="yyyy-MM-dd"
                      value={form.date}
                    >
                      <Validator>
                        <RequiredRule message="Заполните поле Дата" />
                      </Validator>
                    </DateBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={4}>
                  <AppFieldBox label="Статья списания" labelTop>
                    <SelectBox
                      stylingMode="underlined"
                      items={cashFlowAccounts}
                      displayExpr="name"
                      valueExpr="id"
                      value={form.cashFlowAccount}
                      searchEnabled
                      onItemClick={(e) => {
                        setForm((prev) => ({ ...prev, cashFlowAccount: e.itemData.id }));
                      }}
                    >
                      <DropDownOptions title="Выберите Статья списания" closeOnOutsideClick={true} showTitle={false} />
                      <Validator>
                        <RequiredRule message="Выберите Статья списания" />
                      </Validator>
                    </SelectBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={4}>
                  <AppFieldBox label="Сумма" labelTop>
                    <NumberBox
                      stylingMode="underlined"
                      placeholder="0"
                      format={MONEY_FORMAT}
                      value={form.sum}
                      onValueChanged={(e) => {
                        setForm((prev) => ({ ...prev, sum: e.value }));
                      }}
                    >
                      <Validator>
                        <RequiredRule message="Заполните поле Сумма" />
                      </Validator>
                    </NumberBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={8}>
                  <AppFieldBox label="Контрагент" labelTop>
                    <SelectBox
                      stylingMode="underlined"
                      placeholder="Выберите"
                      mode="search"
                      searchEnabled
                      value={form.contractorId}
                      onValueChange={(e) => {
                        setForm((prev) => ({ ...prev, contractorId: e }));
                      }}
                      displayExpr="name"
                      valueExpr="id"
                      dataSource={contractorDataStore}
                    >
                      <Validator>
                        <RequiredRule message="Выберите Контрагент" />
                      </Validator>
                    </SelectBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={4}>
                  <AppFieldBox label="Статья движения ДС" labelTop>
                    <SelectBox
                      stylingMode="underlined"
                      dataSource={requestDateSource}
                      displayExpr="name"
                      valueExpr="id"
                      value={form.cashFlowItemId}
                      searchEnabled
                      onItemClick={(e) => {
                        setForm((prev) => ({ ...prev, cashFlowItemId: e.itemData.id }));
                      }}
                    >
                      <DropDownOptions
                        title="Выберите Статья движения ДС"
                        closeOnOutsideClick={true}
                        showTitle={false}
                      />
                      <Validator>
                        <RequiredRule message="Выберите Статья движения ДС" />
                      </Validator>
                    </SelectBox>
                  </AppFieldBox>
                </AppCol>

                <AppCol col={12}>
                  <AppFieldBox label="Комментарий" labelTop>
                    <TextArea
                      stylingMode="underlined"
                      placeholder="Введите комментарий"
                      mode="text"
                      value={form.comment || ''}
                      onChange={(e: any) => {
                        setForm((prev) => ({ ...prev, comment: e.event.currentTarget.value }));
                      }}
                    />
                  </AppFieldBox>
                </AppCol>
              </Item>
            </Form>
          </div>
        </ValidationGroup>
      </form>
    </div>
  );
};

export default CashFlowExpenseAccount;
